import type { RouteLocationNormalizedGeneric } from 'vue-router';
import { CONFIRMED_PAYMENT_STATUSES } from '~/constants/booking';
import { PAYMENT_REDIRECTED_QUERY_PARAM } from '~/types/payment';

/** A return from the bank, or a navigation staying on the booking's pay page (the marker drop): the server has the booking, not the store. */
function isReturnFromBankOrStay(to: RouteLocationNormalizedGeneric, from: RouteLocationNormalizedGeneric): boolean {
  if (!to.params.bookingId) return false;
  if (to.query[PAYMENT_REDIRECTED_QUERY_PARAM] === 'true') return true;
  return from.name === to.name && from.params.bookingId === to.params.bookingId;
}

/**
 * Route middleware guard for booking payment page.
 *
 * Validates that the user has:
 * - A valid quotation from the booking flow
 * - At least one trip selected (outbound or inbound)
 *
 * Redirects to:
 * - Book trip confirmation page if payment is already completed
 * - Book trip search page if validation fails
 *
 * A return from the bank (3-D Secure) and a navigation staying on the page are let through: the guard
 * protects entering the page, not staying.
 */
export default defineNuxtRouteMiddleware((to, from) => {
  if (isReturnFromBankOrStay(to, from)) return;

  const { bookingDraft, hasQuotation, selectedTrips } = useBookingSelectionStore();
  const isBookingCreated = hasQuotation && selectedTrips.length > 0;

  if (isBookingCreated) return;

  const hasConfirmedPaymentStatus = bookingDraft?.bookingStatus && CONFIRMED_PAYMENT_STATUSES.includes(bookingDraft.paymentStatus);

  const redirectRoute = hasConfirmedPaymentStatus ? {
    name: 'book-confirm-bookingId',
    params: { bookingId: bookingDraft.id },
    query: { status: bookingDraft.bookingStatus },
  } : { name: 'book-search' };

  const localePath = useLocalePath();
  return navigateTo(localePath(redirectRoute));
});
